/***
**Module Name: StripePayoutsPanel
 **File Name :  StripePayoutsPanel.jsx
 **Project :    SPOT SAVER
 **Copyright(c): SPOT SAVER.
 **Organization: SPOT SAVER
 **author :     Hari
 **license :
 **version :    1.0.0
 **Created on : Sep 04 2026
 **Last modified on:
 **Description: The PAYOUTS panel on View Client — the same status the Clients table
 **             shows as a pill, opened out into the facts an admin needs when a venue
 **             calls to ask why they have not been paid: which account, which bank,
 **             which schedule, and what Stripe is still waiting for.
 **
 **             Read-only by design. Nothing here can start onboarding or move money;
 **             both of those belong to the venue's own Stripe flow, and an admin button
 **             that appeared to do them from this page would be lying about what it did.
 ***/
import React from "react";
import moment from "moment";
import {
    stripeStatusMeta, formatBank, formatSchedule, formatRequirement,
    HEADING, MUTED, BORDER
} from "./stripeStatusMeta";

const Row = ({ label, value, id, mono }) => (
    <div className="col-md-4 mb-3" id={id}>
        <div style={{ color: MUTED, fontSize: 12, fontWeight: 600, letterSpacing: "0.4px", textTransform: "uppercase" }}>
            {label}
        </div>
        <div
            style={{
                color: HEADING,
                fontSize: 14,
                fontWeight: 600,
                wordBreak: "break-all",
                fontFamily: mono ? "monospace" : "inherit"
            }}
        >
            {value}
        </div>
    </div>
);

const StripePayoutsPanel = ({ stripe }) => {
    const meta = stripeStatusMeta(stripe);

    // The API build in front of this page does not report Stripe status. Rendering a
    // panel would mean inventing one, so the section simply is not there.
    if (!meta) return null;

    const bank = formatBank(stripe);
    const schedule = formatSchedule(stripe);
    // What the admin asked for vs what Stripe has. Only worth saying when they differ —
    // that gap is a schedule change Stripe never accepted, and it is invisible anywhere
    // else in the admin.
    const scheduleMismatch = stripe.payoutSchedule
        && stripe.scheduleInterval
        && stripe.payoutSchedule !== stripe.scheduleInterval;
    const requirements = Array.isArray(stripe.requirementsDue) ? stripe.requirementsDue : [];

    return (
        // clear + a top margin because the Status row above is floated by the admin
        // stylesheet: without them this section rides up into it and the heading lands
        // on top of the Status field.
        <div className="row mt-4" id="stripe-payouts-section" style={{ clear: "both" }}>
            <div className="col-12">
                <h5 className="form-title mb-3">payouts</h5>
                <div
                    className="p-3 rounded-3"
                    id="stripe-payouts-panel"
                    style={{ background: meta.tint, border: `1px solid ${meta.line || BORDER}` }}
                >
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                        <div className="d-flex align-items-center gap-2">
                            <span className="material-icons" style={{ color: meta.color, fontSize: 22 }}>
                                {meta.icon}
                            </span>
                            <span style={{ color: meta.color, fontSize: 16, fontWeight: 700 }} id="stripe-payouts-status">
                                {meta.label}
                            </span>
                        </div>
                        {/* Which Stripe world these numbers are from. Without it a screen
                            full of healthy sandbox accounts reads as a healthy platform. */}
                        <span
                            id="stripe-payouts-mode"
                            style={{ color: MUTED, fontSize: 11, fontWeight: 700, letterSpacing: "1px" }}
                        >
                            {stripe.mode === "production" ? "LIVE" : "SANDBOX"}
                        </span>
                    </div>

                    <p style={{ color: MUTED, fontSize: 13 }} className="mb-3">{meta.blurb}</p>

                    <div className="row" id="stripe-payouts-facts">
                        <Row
                            id="stripe-account-row"
                            label="Stripe account"
                            mono
                            value={stripe.stripeAccountId || "Not created"}
                        />
                        <Row
                            id="stripe-bank-row"
                            label="Bank on file"
                            value={bank || "None on file"}
                        />
                        <Row
                            id="stripe-schedule-row"
                            label="Payout schedule"
                            value={schedule || "Stripe default"}
                        />
                        <Row
                            id="stripe-charges-row"
                            label="Card charges"
                            value={stripe.chargesEnabled ? "Enabled" : "Disabled"}
                        />
                        <Row
                            id="stripe-onboarding-row"
                            label="Onboarding"
                            value={stripe.onboardingCompleted ? "Completed" : "Not completed"}
                        />
                        <Row
                            id="stripe-checked-row"
                            label="Status as of"
                            value={stripe.statusUpdatedAt
                                ? moment.utc(stripe.statusUpdatedAt).local().format("MMM D, YYYY, h:mm A")
                                : "Never checked"}
                        />
                    </div>

                    {scheduleMismatch && (
                        <div className="alert alert-secondary mb-2" role="alert" id="stripe-schedule-mismatch">
                            This client is set to <strong>{stripe.payoutSchedule}</strong> settlement, but Stripe is
                            paying the account <strong>{stripe.scheduleInterval}</strong>. The last schedule change
                            did not reach Stripe.
                        </div>
                    )}

                    {stripe.disabledReason ? (
                        <div className="alert alert-danger mb-2" role="alert" id="stripe-disabled-reason">
                            <strong>Stripe is holding payouts:</strong> {stripe.disabledReason}
                        </div>
                    ) : null}

                    {requirements.length > 0 && (
                        <div className="alert alert-warning mb-0" role="alert" id="stripe-requirements-due">
                            <strong>Stripe is still asking the client for:</strong>{" "}
                            {requirements.map(formatRequirement).join(", ")}.
                        </div>
                    )}
                </div>

                {/* Said once, plainly: this is the last thing Stripe told us, not a live
                    read. An admin about to tell a venue "your payouts are fine" should
                    know which of those two they are looking at. */}
                <p style={{ color: MUTED, fontSize: 12 }} className="mt-2 mb-0">
                    Updated from Stripe whenever the account changes. The client's own
                    dashboard is the live view.
                </p>
            </div>
        </div>
    );
};

export default StripePayoutsPanel;
